import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { Flex, Modal } from 'antd';
import { ChartTooltip, ChartTitle, ChartSegment, MeteorEchart, Loading, ECHART_EVENT } from '@meteor/frontend-core';
import ChartContent from './widgets/chart-content';
import { HolderOutlined } from '@ant-design/icons';
import ChartEmpty from './widgets/chart-empty';

type ChartProps = {
  chartOptions: any;
  contentList: any;
  segment: any;
  title: {
    value: any;
    styles: CSSProperties;
  };
  titleTooltips?: string;
  height?: number | string;
  tooltips?: any[];
  showSeriesLabel?: boolean;
  loading?: boolean;
  isBank?: boolean;
  chartRef?: any;
  modalChartRef?: any;
  onReset?: Function;
  drag?: boolean;
  opts?: { renderer: 'canvas' | 'svg' };
  onOpen?: () => void;
  onClick?: (params) => void;
  mouseoverQuery?: string;
  onMouseover?: (params) => void;
  onMouseout?: (params) => void;
  onMousemove?: (params) => void;
  onFitView?: (params) => void;
  onSegmentChange?: (segment) => void;
  onHighlight?: (params) => void;
  onDownplay?: (params) => void;
  onDatazoom?: (params) => void;
  onGlobalout?: (params) => void;
  onLegendSelectChanged?: (params) => void;
};

const SingleSegmentChart: React.FC<ChartProps> = (props) => {
  const {
    tooltips,
    chartOptions,
    title,
    titleTooltips,
    contentList,
    height,
    segment,
    showSeriesLabel = false,
    loading = false,
    isBank = false,
    chartRef,
    modalChartRef,
    onReset,
    drag,
    opts = { renderer: 'svg' },
    onOpen,
    onClick,
    onMouseover,
    onMouseout,
    onMousemove,
    onFitView,
    onSegmentChange,
    onHighlight,
    onDownplay,
    onDatazoom,
    onGlobalout,
    onLegendSelectChanged,
  } = props;
  const [showLabel, setShowLabel] = useState(showSeriesLabel);
  const [segmentSelector, setSegmentSelector] = useState<any>(segment.default);
  const [chartOption, setChartOption] = useState<any>(chartOptions[segment.default]);
  const chartSelfRef: any = useRef(null);
  const modalChartSelfRef = useRef(null);
  const currentRef = chartRef || chartSelfRef;
  const modelCurrentRef = modalChartRef || modalChartSelfRef;

  const [isModalOpen, setIsModalOpen] = useState(false);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  let modalHeight = 500;
  let modalWidth = 500;
  if (typeof window !== 'undefined') {
    modalHeight = document.body.clientHeight * 0.7;
    modalWidth = document.body.clientWidth * 0.7;
  }

  const getOptions = (options: any) => {
    if (options.series) {
      options.series.forEach((series: any) => {
        if (series) {
          series.label = series.label || {};
          series.label.show = showLabel;
        }
      });
    }
    return {
      ...options,
    };
  };

  useEffect(() => {
    setSegmentSelector(segment.default);
  }, [segment.default]);

  useEffect(() => {
    const op = getOptions(chartOptions[segmentSelector]);
    setChartOption(op);
    if (currentRef && currentRef.current) {
      const inst = currentRef.current.getEchartsInstance();
      if (inst) {
        inst.setOption(op, true);
      }
    }
    if (modelCurrentRef && modelCurrentRef.current) {
      const instance = modelCurrentRef.current.getEchartsInstance();
      if (instance) {
        instance.setOption(op, true);
      }
    }
  }, [segmentSelector, chartOptions, showLabel]);

  useEffect(() => {
    if (onSegmentChange) {
      onSegmentChange(segmentSelector);
    }
  }, [segmentSelector]);

  useEffect(() => {
    const instance = modelCurrentRef.current?.getEchartsInstance();
    if (instance) {
      instance.resize();
    }
    if (isModalOpen && onOpen) {
      onOpen();
    }
  }, [isModalOpen]);

  useEffect(() => {
    setTimeout(() => {
      if (!currentRef || !currentRef.current) {
        return;
      }
      const instance = currentRef.current.getEchartsInstance();

      instance.off('legendselectchanged');
      instance.on('legendselectchanged', (event: any) => {
        if (onLegendSelectChanged) {
          onLegendSelectChanged(event);
        }
      });

      instance.off('click');
      instance.on('click', (event: any) => {
        if (event.componentType === 'series') {
          if (onClick) {
            onClick(event);
          }
        }
      });

      instance.off('mouseover');
      instance.on('mouseover', (event: any) => {
        if (onMouseover) {
          onMouseover(event);
        }
      });
      instance.off('mouseout');
      instance.on('mouseout', (event: any) => {
        if (onMouseout) {
          onMouseout(event);
        }
      });
      instance.off('mousemove');
      instance.on('mousemove', (event: any) => {
        if (onMousemove) {
          onMousemove(event);
        }
      });
      instance.off('highlight');
      instance.on('highlight', (event: any) => {
        if (onHighlight) {
          onHighlight(event);
        }
      });
      instance.off('downplay');
      instance.on('downplay', (event: any) => {
        if (onDownplay) {
          onDownplay(event);
        }
      });
      instance.off('datazoom');
      instance.on('datazoom', (event: any) => {
        if (onDatazoom) {
          onDatazoom(event);
        }
      });
      instance.off('globalout');
      instance.on('globalout', (event: any) => {
        if (onGlobalout) {
          onGlobalout(event);
        }
      });
    }, 800);
  }, [currentRef.current]);

  useEffect(() => {
    setTimeout(() => {
      if (!modelCurrentRef || !modelCurrentRef.current) {
        return;
      }
      const instance = modelCurrentRef.current.getEchartsInstance();

      instance.off('legendselectchanged');
      instance.on('legendselectchanged', (event: any) => {
        if (onLegendSelectChanged) {
          onLegendSelectChanged(event);
        }
      });

      instance.off('click');
      instance.on('click', (event: any) => {
        if (event.componentType === 'series') {
          if (onClick) {
            onClick(event);
          }
        }
      });

      instance.off('mouseover');
      instance.on('mouseover', (event: any) => {
        if (onMouseover) {
          onMouseover(event);
        }
      });
      instance.off('mouseout');
      instance.on('mouseout', (event: any) => {
        if (onMouseout) {
          onMouseout(event);
        }
      });
      instance.off('mousemove');
      instance.on('mousemove', (event: any) => {
        if (onMousemove) {
          onMousemove(event);
        }
      });
      instance.off('highlight');
      instance.on('highlight', (event: any) => {
        if (onHighlight) {
          onHighlight(event);
        }
      });
      instance.off('downplay');
      instance.on('downplay', (event: any) => {
        if (onDownplay) {
          onDownplay(event);
        }
      });
      instance.off('datazoom');
      instance.on('datazoom', (event: any) => {
        if (onDatazoom) {
          onDatazoom(event);
        }
      });
      instance.off('globalout');
      instance.on('globalout', (event: any) => {
        if (onGlobalout) {
          onGlobalout(event);
        }
      });
    }, 800);
  }, [modelCurrentRef.current, isModalOpen]);

  const onTooltipClick = (type: string, show: boolean = false) => {
    if (type === 'zoomIn') {
      showModal();
    }
    if (type === 'reset') {
      reset();
    }
    if (type === 'label') {
      setShowLabel(show);
    }
    if (type === 'fitView') {
      const actionEvent = {
        eventType: ECHART_EVENT.CHART_FIT_VIEW,
        chartTitle: title,
      };
      if (onFitView) {
        onFitView(actionEvent);
      }
    }
  };

  const reset = () => {
    if (onReset) {
      onReset();
    }
  };

  return (
    <>
      <div className="summary-kpi-card">
        <ChartTooltip items={tooltips || ['zoomIn']} showSeriesLabel={showSeriesLabel} onClick={onTooltipClick} />
        <Flex justify="space-between">
          <Flex>
            {drag && <HolderOutlined style={{ height: 24, marginTop: 2, marginRight: 6, cursor: 'move' }} />}
            <ChartTitle title={title} titleTooltips={titleTooltips} />
          </Flex>
          {segment.list.length > 1 && (
            <div style={{ marginRight: 10 }}>
              <ChartSegment
                segment={segment}
                currentValue={segmentSelector}
                onChange={(value: any) => {
                  setSegmentSelector(value);
                }}
              />
            </div>
          )}
        </Flex>
        {!loading ? (
          <>
            {isBank ? (
              <ChartEmpty />
            ) : (
              <>
                <ChartContent contentList={contentList} />

                <MeteorEchart opts={opts} chartRef={currentRef} option={chartOption} style={{ height: height || 160 }} />
              </>
            )}
          </>
        ) : (
          <Loading fixed={false} />
        )}
      </div>

      <Modal
        className="aipcmc"
        title={
          <Flex justify="space-between">
            <span>{title.value}</span>
            {segment.list.length > 1 && (
              <div style={{ marginRight: 30 }}>
                <ChartSegment
                  segment={segment}
                  currentValue={segmentSelector}
                  onChange={(value: any) => {
                    setSegmentSelector(value);
                  }}
                />
              </div>
            )}
          </Flex>
        }
        open={isModalOpen}
        onCancel={handleCancel}
        width={modalWidth + 100}
        footer={null}
      >
        <div className="summary-kpi-modal">
          <div className="summary-kpi-card">
            <ChartTooltip
              items={
                tooltips
                  ? tooltips.filter((item) => {
                      return item !== 'zoomIn';
                    })
                  : ['reset']
              }
              showSeriesLabel={showSeriesLabel}
              onClick={onTooltipClick}
            />
            <ChartContent contentList={contentList} />

            <MeteorEchart opts={opts} chartRef={modelCurrentRef} option={chartOption} style={{ height: modalHeight }} />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default SingleSegmentChart;
